/**
 * App-wide tooltips for icon-only controls — **one delegated layer**, like the UI click SFX
 * (`sfx/uiFeedback.ts`). A control opts in with data attributes instead of wrapping itself in a
 * component, so a button keeps its own markup and any surface can take part:
 *
 * - `data-tip`: the headline, usually the control's name (`Measure`). Required.
 * - `data-tip-key`: the shortcut, drawn as a keycap beside the headline (`hold M`, `Ctrl/⌘+Z`).
 * - `data-tip-hint`: one line on what it does (`press a cell or unit, then drag or click the end`).
 * - `data-tip-side`: where the tip opens, `bottom` by default. It flips to the opposite side when
 *   it would leave the viewport, and slides along its side to stay on screen.
 *
 * {@link tipAttrs} builds the set. A control carrying them should drop its native `title`, or the
 * browser's own tooltip shows as well.
 *
 * **When it shows.** Mouse or pen hover, after {@link SHOW_DELAY_MS}, or at once while a tip closed
 * within {@link WARM_MS} (running the pointer down the tool rail). Keyboard focus, at once, when the
 * browser marks the focus visible. Touch never shows a tip and never delays a tap. A press hides
 * the tip, and it stays hidden until the pointer leaves that control, so it never covers what the
 * press opened. Esc, a scroll, and the control leaving the page hide it too. Esc is not consumed:
 * the combat Esc layers (`decisions/0189`) still see it.
 *
 * **Accessibility.** The tip is one `role="tooltip"` element; while it shows, the control's
 * `aria-describedby` names it. The control's accessible name stays its own `aria-label`.
 */
import "./tooltip.css";

export type TipSide = "top" | "bottom" | "left" | "right";

export type TipSpec = { label: string; key?: string; hint?: string; side?: TipSide };

export type TipAttrs = {
  "data-tip": string;
  "data-tip-key"?: string;
  "data-tip-hint"?: string;
  "data-tip-side"?: TipSide;
};

/** The data attributes for a tooltip, to spread onto the control. */
export function tipAttrs({ label, key, hint, side }: TipSpec): TipAttrs {
  const attrs: TipAttrs = { "data-tip": label };
  if (key) attrs["data-tip-key"] = key;
  if (hint) attrs["data-tip-hint"] = hint;
  if (side) attrs["data-tip-side"] = side;
  return attrs;
}

export const SHOW_DELAY_MS = 350;
export const WARM_MS = 500;
const GAP = 8;
const MARGIN = 6;
const TIP_ID = "app-tip";

type Box = { left: number; top: number; width: number; height: number };

const OPPOSITE: Record<TipSide, TipSide> = { top: "bottom", bottom: "top", left: "right", right: "left" };

/**
 * Where the tip goes: on `side` of `anchor`, `GAP` px off it, centred along that side. It takes the
 * opposite side when `side` has no room and the opposite does, then slides to stay `MARGIN` px inside
 * the viewport. Pure, so it is unit-tested without a layout engine.
 */
export function placeTip({ anchor, tip, viewport, side }: { anchor: Box; tip: { width: number; height: number }; viewport: { width: number; height: number }; side: TipSide }): { left: number; top: number; side: TipSide } {
  const fits = (s: TipSide) => {
    if (s === "top") return anchor.top - GAP - tip.height >= MARGIN;
    if (s === "bottom") return anchor.top + anchor.height + GAP + tip.height <= viewport.height - MARGIN;
    if (s === "left") return anchor.left - GAP - tip.width >= MARGIN;
    return anchor.left + anchor.width + GAP + tip.width <= viewport.width - MARGIN;
  };
  const chosen = !fits(side) && fits(OPPOSITE[side]) ? OPPOSITE[side] : side;
  let left: number;
  let top: number;
  if (chosen === "top" || chosen === "bottom") {
    left = anchor.left + anchor.width / 2 - tip.width / 2;
    top = chosen === "top" ? anchor.top - GAP - tip.height : anchor.top + anchor.height + GAP;
  } else {
    top = anchor.top + anchor.height / 2 - tip.height / 2;
    left = chosen === "left" ? anchor.left - GAP - tip.width : anchor.left + anchor.width + GAP;
  }
  const clamp = (v: number, size: number, room: number) => Math.max(MARGIN, Math.min(v, room - size - MARGIN));
  return { left: clamp(left, tip.width, viewport.width), top: clamp(top, tip.height, viewport.height), side: chosen };
}

/** The nearest element carrying a `data-tip`, from an event target. */
function tipTarget(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  return target.closest<HTMLElement>("[data-tip]");
}

function isSide(v: string | undefined): v is TipSide {
  return v === "top" || v === "bottom" || v === "left" || v === "right";
}

/**
 * Mount the tooltip layer once, at app boot (`main.tsx`). Returns an unmount function (for tests
 * and HMR; in the app it lives as long as the page).
 */
export function installTooltips(root: Document = document, opts: { delayMs?: number } = {}): () => void {
  const delayMs = opts.delayMs ?? SHOW_DELAY_MS;
  const win = root.defaultView ?? window;
  let tip: HTMLDivElement | null = null;
  let current: HTMLElement | null = null;
  let priorDescribedBy: string | null = null;
  let pending: { el: HTMLElement; timer: ReturnType<typeof setTimeout> } | null = null;
  let suppressed: HTMLElement | null = null;
  let hiddenAt = -Infinity;
  const observer = new win.MutationObserver(() => {
    if (current && !current.isConnected) hide();
  });

  const tipEl = () => {
    if (tip) return tip;
    tip = root.createElement("div");
    tip.id = TIP_ID;
    tip.className = "app-tip";
    tip.setAttribute("role", "tooltip");
    tip.hidden = true;
    root.body.appendChild(tip);
    return tip;
  };

  const cancel = () => {
    if (pending) clearTimeout(pending.timer);
    pending = null;
  };

  function hide() {
    cancel();
    if (!current) return;
    if (priorDescribedBy === null) current.removeAttribute("aria-describedby");
    else current.setAttribute("aria-describedby", priorDescribedBy);
    current = null;
    observer.disconnect();
    if (tip) tip.hidden = true;
    hiddenAt = Date.now();
  }

  function show(el: HTMLElement) {
    cancel();
    if (current === el) return;
    hide();
    const label = el.dataset.tip;
    if (!label || !el.isConnected) return;
    const t = tipEl();
    t.replaceChildren();
    const head = root.createElement("div");
    head.className = "app-tip-head";
    const name = root.createElement("span");
    name.className = "app-tip-label";
    name.textContent = label;
    head.appendChild(name);
    if (el.dataset.tipKey) {
      const key = root.createElement("kbd");
      key.className = "app-tip-key";
      key.textContent = el.dataset.tipKey;
      head.appendChild(key);
    }
    t.appendChild(head);
    if (el.dataset.tipHint) {
      const hint = root.createElement("div");
      hint.className = "app-tip-hint";
      hint.textContent = el.dataset.tipHint;
      t.appendChild(hint);
    }
    t.style.left = "0px";
    t.style.top = "0px";
    t.hidden = false;
    const side = isSide(el.dataset.tipSide) ? el.dataset.tipSide : "bottom";
    const r = el.getBoundingClientRect();
    const at = placeTip({
      anchor: { left: r.left, top: r.top, width: r.width, height: r.height },
      tip: { width: t.offsetWidth, height: t.offsetHeight },
      viewport: { width: win.innerWidth, height: win.innerHeight },
      side,
    });
    t.style.left = `${at.left}px`;
    t.style.top = `${at.top}px`;
    t.dataset.side = at.side;
    current = el;
    priorDescribedBy = el.getAttribute("aria-describedby");
    el.setAttribute("aria-describedby", priorDescribedBy ? `${priorDescribedBy} ${TIP_ID}` : TIP_ID);
    observer.observe(root.body, { childList: true, subtree: true });
  }

  const onPointerOver = (e: PointerEvent) => {
    if (e.pointerType === "touch") return;
    const el = tipTarget(e.target);
    if (!el || el === current || el === suppressed || pending?.el === el) return;
    cancel();
    const warm = current !== null || Date.now() - hiddenAt < WARM_MS;
    if (warm || delayMs === 0) show(el);
    else pending = { el, timer: setTimeout(() => show(el), delayMs) };
  };
  const onPointerOut = (e: PointerEvent) => {
    const el = tipTarget(e.target);
    if (!el) return;
    const into = e.relatedTarget instanceof Node && el.contains(e.relatedTarget);
    if (into) return;
    if (pending?.el === el) cancel();
    if (suppressed === el) suppressed = null;
    if (current === el) hide();
  };
  const onPointerDown = (e: PointerEvent) => {
    cancel();
    const el = tipTarget(e.target);
    if (el) suppressed = el;
    hide();
  };
  const onFocusIn = (e: FocusEvent) => {
    const el = tipTarget(e.target);
    if (!el || el === suppressed) return;
    // A mouse press focuses a button too; only a keyboard-visible focus shows the tip.
    if (el.matches(":focus-visible")) show(el);
  };
  const onFocusOut = (e: FocusEvent) => {
    const el = tipTarget(e.target);
    if (el && el === current) hide();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") hide();
  };
  const onScroll = () => hide();

  root.addEventListener("pointerover", onPointerOver);
  root.addEventListener("pointerout", onPointerOut);
  root.addEventListener("pointerdown", onPointerDown, true);
  root.addEventListener("focusin", onFocusIn);
  root.addEventListener("focusout", onFocusOut);
  root.addEventListener("keydown", onKeyDown, true);
  root.addEventListener("scroll", onScroll, true);
  return () => {
    hide();
    root.removeEventListener("pointerover", onPointerOver);
    root.removeEventListener("pointerout", onPointerOut);
    root.removeEventListener("pointerdown", onPointerDown, true);
    root.removeEventListener("focusin", onFocusIn);
    root.removeEventListener("focusout", onFocusOut);
    root.removeEventListener("keydown", onKeyDown, true);
    root.removeEventListener("scroll", onScroll, true);
    tip?.remove();
    tip = null;
  };
}
